// A note you can correct where you read it.
//
// The same gap kept appearing: a note is written in one place, read in
// another, and the only way to fix a typo was a separate admin screen most
// people did not know existed — so the note simply stayed wrong. This is the
// shared editor behind the activity log, task notes and visit care notes.
//
// It edits the note and nothing else. Statuses, times and stage changes beside
// a note are the record of what happened, not a comment, so they are corrected
// by recording the real change rather than by rewriting history.
import { useState } from 'react';

export default function InlineNote({
  value,
  onSave,                 // (text) => Promise, throws on failure
  canEdit = false,
  size = 13,
  placeholder = 'No note',
  minHeight = 64,
  footer = null,          // provenance line, e.g. "Edited by …"
  editLabel = 'Edit this note',
}) {
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState(value || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const open = () => { setText(value || ''); setError(''); setEditing(true); };
  const cancel = () => { setText(value || ''); setError(''); setEditing(false); };
  const save = async () => {
    setBusy(true); setError('');
    try { await onSave(text.trim()); setEditing(false); }
    catch (e) { setError(e.message || 'Could not save'); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <div style={{ marginBottom: 4 }}>
        <textarea
          value={text}
          onChange={e => setText(e.target.value)}
          autoFocus
          style={{ width: '100%', minHeight, padding: '8px 10px', borderRadius: 7, border: '1px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontSize: size, fontFamily: 'inherit', boxSizing: 'border-box', lineHeight: 1.5 }}
        />
        {error && <div style={{ fontSize: 11.5, color: 'var(--danger)', marginTop: 4 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
          <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} disabled={busy} onClick={save}>{busy ? 'Saving…' : 'Save'}</button>
          <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} disabled={busy} onClick={cancel}>Cancel</button>
        </div>
      </div>
    );
  }

  return (
    <>
      <div style={{ fontSize: size, color: 'var(--text2)', marginBottom: 4, display: 'flex', gap: 8, alignItems: 'flex-start' }}>
        <span style={{ flex: 1, whiteSpace: 'pre-wrap', minWidth: 0 }}>
          {value || <span style={{ color: 'var(--text3)', fontStyle: 'italic' }}>{placeholder}</span>}
        </span>
        {canEdit && (
          <button
            onClick={open}
            title={editLabel}
            style={{ flexShrink: 0, background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, color: 'var(--accent2)' }}>
            Edit
          </button>
        )}
      </div>
      {footer}
    </>
  );
}
